<script setup lang="ts">
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { getInputStateClassesV2 } from '~kui/composables/useInputState';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
  TagsInputRoot,
  useFilter,
} from 'reka-ui';
import { computed, ref, watch } from 'vue';

import type { ComboboxProps } from '../types';

import { contentClasses, emptyClasses, itemClasses, viewportClasses } from '../classList';
import { comboboxDefaults } from '../types';
import { useComboboxClasses, useComboboxMultiple, useComboboxState, useComboboxValue } from '../useCombobox';

const props = withDefaults(defineProps<ComboboxProps>(), comboboxDefaults);

const emit = defineEmits<{
  blur: [];
  input: [value: string];
  clear: [];
}>();

const model = defineModel<(string | number)[]>({ required: true });

// Use the composable utilities
const { hasValueMultiple } = useComboboxValue();
const hasValue = hasValueMultiple(model.value);
const { open, getOptionLabel, removeItem, clearSelection } = useComboboxMultiple(props, model);
const { inputState } = useComboboxState(props, hasValue);
const { classes: v1AnchorClasses } = useComboboxClasses(inputState, 'anchor');

const elementId = useId();
const hasError = computed(() => props.errorMessages.length > 0);
const errorsId = computed(() => `error-${elementId}`);

const isV2 = computed(() => props.version === 'v2');

// v2 mirrors KUICoreInputText's box (same wrapper/font classes) so the anchor
// lines up with text inputs on the same form; min-h over fixed h so the box can
// still grow when selected tags wrap.
const { wrapperClasses: v2AnchorClasses, fontClasses: v2FontClasses } = getInputStateClassesV2({
  disabled: computed(() => props.disabled ?? false),
  state: computed(() => (hasError.value ? 'error' : props.inputTypeState)),
  wrapperClasses: ['h-auto', 'min-h-14', 'pe-3'],
});
const anchorClasses = computed(() => (isV2.value ? v2AnchorClasses.value : v1AnchorClasses.value));

// v2: ps-3 (+ the TagsInputRoot's ps-1) lands the placeholder at the same 16px
// inset as KUICoreInputText's px-4.
const searchInputClasses = computed(() =>
  twMerge(
    'placeholder-muted-foreground min-w-30 flex-1 rounded bg-transparent! px-3.5 py-2.75 focus:outline-none',
    isV2.value ? twMerge('ps-3 py-3', v2FontClasses.value) : '',
  ),
);

// Emit blur when dropdown closes
watch(open, (newOpen, oldOpen) => {
  if (oldOpen === true && newOpen === false) {
    emit('blur');
  }
});

const onInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('input', target.value);
};

// Also emit blur when component loses focus (e.g., tabbing past)
const handleFocusOut = () => {
  emit('blur');
};

// Multiple-specific logic
const { contains } = useFilter({ sensitivity: 'base' });
const query = ref('');

const filteredOptions = computed(() =>
  props.options.filter(option => contains(option.label, query.value) && option.value !== null && !model.value.includes(option.value)),
);

const onClear = () => {
  clearSelection();
  emit('clear');
};

watch(
  model,
  () => {
    query.value = '';
  },
  { deep: true },
);
</script>

<template>
  <div class="group/kui-input" @focusout="handleFocusOut">
    <ComboboxRoot
      v-model="model"
      v-model:open="open"
      multiple
      ignore-filter
      class="relative"
      :disabled="disabled"
      :data-testid="dataTestId"
    >
      <KUICoreInputLabel
        v-if="!hideLabel"
        :label="label"
        :required="required"
        :class="{ 'mb-2': isV2 }"
      >
        {{ label }}
      </KUICoreInputLabel>
      <ComboboxAnchor :class="anchorClasses" @click.stop="open = true">
        <TagsInputRoot
          :model-value="model.map(String)"
          delimiter=""
          class="flex min-h-10 min-w-0 flex-1 flex-wrap items-center gap-2 py-1 ps-1"
        >
          <TagsInputItem
            v-for="item in model"
            :key="String(item)"
            :value="String(item)"
            class="bg-primary flex items-center justify-center gap-1 rounded-2xl px-2 py-1 text-sm text-primary-foreground"
          >
            <TagsInputItemText>{{ getOptionLabel(item) }}</TagsInputItemText>
            <TagsInputItemDelete class="cursor-pointer" @click="removeItem(item)">
              <Icon :name="ICONS.cancel" size="xs" />
            </TagsInputItemDelete>
          </TagsInputItem>
          <ComboboxInput
            v-model="query"
            as-child
            @input="onInputChange"
          >
            <TagsInputInput
              :placeholder="placeholder"
              :class="searchInputClasses"
              :data-testid="`${dataTestId}-search-input`"
              :aria-invalid="hasError ? 'true' : undefined"
              :aria-describedby="hasError ? errorsId : undefined"
              @keydown.enter.prevent
            />
          </ComboboxInput>
        </TagsInputRoot>
        <div class="flex shrink-0 items-center">
          <ComboboxTrigger
            v-if="!clearable || (clearable && !hasValue)"
            class="data-disabled:cursor-not-allowed"
            :data-testid="`${dataTestId}-trigger`"
            @click.stop
          >
            <KUICoreButtonIcon
              :icon="ICONS.arrowDown"
              size="sm"
              variant="tertiary"
            />
          </ComboboxTrigger>
          <KUICoreClickable
            v-else
            :data-testid="`${dataTestId}-clear-button`"
            @click="onClear"
          >
            <Icon
              :name="ICONS.cancel"
              size="sm"
              class="ms-auto"
            />
          </KUICoreClickable>
        </div>
      </ComboboxAnchor>
      <ComboboxPortal>
        <ComboboxContent
          position="popper"
          class="w-[var(--reka-combobox-trigger-width)]! z-101"
          :class="contentClasses"
        >
          <ComboboxViewport
            :class="viewportClasses"
            class="max-h-64"
            :data-testid="`${dataTestId}-listbox`"
          >
            <ComboboxEmpty :class="emptyClasses">
              <span v-if="loading">{{ $t('unified.common.combobox.loading') }}</span>
              <span v-else>{{ $t('unified.common.combobox.noResultsFound') }}</span>
            </ComboboxEmpty>
            <ComboboxItem
              v-for="option in filteredOptions"
              :key="String(option.value)"
              :value="option.value!"
              :class="itemClasses"
            >
              <ComboboxItemIndicator class="absolute left-2 inline-flex items-center justify-center">
                <Icon :name="ICONS.tick" size="sm" />
              </ComboboxItemIndicator>
              <span class="pl-6">{{ option.label }}</span>
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxPortal>
    </ComboboxRoot>
    <KUICoreInputErrors
      v-if="errorMessages.length"
      :id="errorsId"
      :error-messages="errorMessages"
    />
  </div>
</template>
